import React, { useState, useRef } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Tabs, 
  Tab, 
  TextField,
  useTheme,
  alpha,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import { useNavigate } from 'react-router-dom';
import IndianGSTInvoiceTemplate from '../components/templates/IndianGSTInvoiceTemplate';
import AkMoInvoicingFormat, { 
  BusinessDetails, 
  ClientDetails,
  DeliveryDetails,
  InvoiceDetails,
  InvoiceItem
} from '../components/templates/AkMoInvoicingFormat';
import { exportInvoiceToPdf } from '../utils/invoicePdfExporter';
import { Download as DownloadIcon, Refresh as RefreshIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
// These imports are used in the exportInvoiceToPdf utility, but not directly in this file
// import html2canvas from "html2canvas";
// import jsPDF from "jspdf";

// Sample data for previews
const sampleBusinessDetails: BusinessDetails = {
  name: "AkMo Technologies Pvt. Ltd.",
  address: "123 Main Street, Tech Park",
  city: "Bangalore",
  state: "Karnataka",
  pincode: "560001",
  gstin: "29AABCA1234D1Z5",
  phone: "+91 9876543210",
  email: "accounts@akmotech.com",
  bankName: "HDFC Bank",
  bankBranch: "Koramangala Branch",
  accountNumber: "123456789012345",
  ifsc: "HDFC0001234",
  upiId: "accounts@akmotech",
  gstRegistered: "registered",
  gstType: "regular"
};

// Create a business details object specifically for GST template
const businessDetailsForGST = {
  name: "AkMo Technologies Pvt. Ltd.",
  address: "123 Main Street, Tech Park",
  city: "Bangalore",
  state: "Karnataka",
  pincode: "560001",
  gstin: "29AABCA1234D1Z5", // Required field for GST template
  phone: "+91 9876543210",
  email: "accounts@akmotech.com",
  bankName: "HDFC Bank", 
  accountNumber: "123456789012345",
  ifscCode: "HDFC0001234",
  upiId: "accounts@akmotech",
  ifsc: "HDFC0001234", // Adding the ifsc property to match the BusinessDetails interface
  gstRegistered: "registered",
  gstType: "regular"
};

const sampleClientDetails: ClientDetails = {
  name: "ABC Enterprises",
  address: "456 Business Avenue",
  city: "Mumbai",
  state: "Maharashtra",
  pincode: "400001",
  gstin: "27AADCS1234A1Z9",
  phone: "+91 9876543211",
  email: "info@abcenterprises.com"
};

const sampleDeliveryDetails: DeliveryDetails = {
  name: "ABC Enterprises - Warehouse",
  address: "789 Industrial Zone",
  city: "Mumbai",
  state: "Maharashtra",
  pincode: "400003",
  phone: "+91 9876543212",
  email: "warehouse@abcenterprises.com",
  contactPerson: "Rajesh Kumar",
  contactNumber: "+91 9876543213"
};

const sampleInvoiceDetails: InvoiceDetails = {
  invoiceNumber: "INV-2023-0001",
  invoiceDate: "05/04/2023",
  dueDate: "20/04/2023",
  purchaseRefNumber: "PO-2023-0123",
  discount: 2000,
  additionalCharges: [
    { name: "Shipping", amount: 500 },
    { name: "Packaging", amount: 300 }
  ],
  termsAndConditions: [
    "Payment to be made within 15 days from the date of invoice.",
    "Goods once sold cannot be returned or exchanged.",
    "Interest at 18% p.a. will be charged on overdue payments.",
    "All disputes are subject to Bangalore jurisdiction only."
  ]
};

const sampleInvoiceItems: InvoiceItem[] = [
  {
    productName: "Web Development Services",
    description: "E-commerce website development including payment gateway integration",
    quantity: 1,
    unit: "Project",
    hsn: "998314",
    gstRate: 18,
    taxableValue: 50000,
    taxAmount: 9000,
    total: 59000
  },
  {
    productName: "Server Hosting (Annual)",
    description: "Cloud server hosting with daily backups",
    quantity: 1,
    unit: "Year",
    hsn: "998316",
    gstRate: 18,
    taxableValue: 12000,
    taxAmount: 2160,
    total: 14160
  }
];

// Sample data for Indian GST Template
const sampleGSTInvoice = {
  id: "gst-invoice-001",
  invoiceNumber: "INV-2023-0001",
  client: "ABC Enterprises",
  clientId: "client-001",
  clientDetails: {
    name: "ABC Enterprises",
    address: "456 Business Avenue, Mumbai, Maharashtra - 400001",
    gstin: "27AADCS1234A1Z9",
    phone: "+91 9876543211",
    email: "info@abcenterprises.com",
    state: "Maharashtra"
  },
  date: "2023-04-05",
  dueDate: "2023-04-20",
  items: [
    {
      id: 1,
      name: "Web Development Services",
      description: "E-commerce website development including payment gateway integration",
      quantity: 1,
      rate: 50000,
      amount: 59000,
      hsnCode: "998314",
      gstRate: 18
    },
    {
      id: 2,
      name: "Server Hosting (Annual)",
      description: "Cloud server hosting with daily backups",
      quantity: 1,
      rate: 12000,
      amount: 14160,
      hsnCode: "998316",
      gstRate: 18
    }
  ],
  notes: "Thank you for your business. Please make payment by the due date.",
  termsAndConditions: "Payment to be made within 15 days from the date of invoice.",
  taxType: "GST",
  taxRate: 18,
  taxable: true,
  discountType: "fixed",
  discountValue: 2000,
  discount: 2000,
  subtotal: 62000,
  tax: 11160,
  amount: 71160,
  status: "draft" as "draft" | "pending" | "paid" | "overdue" | "deleted",
  createdAt: new Date().toISOString(),
  userId: "user-001",
  bankDetails: {
    bankName: "HDFC Bank",
    accountNumber: "123456789012345",
    ifscCode: "HDFC0001234"
  },
  placeOfSupply: "Maharashtra",
  reverseCharge: "N"
};

// Tab Panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`invoice-template-tabpanel-${index}`}
      aria-labelledby={`invoice-template-tab-${index}`}
      {...other}
      style={{ height: 'auto', minHeight: '100%' }}
    >
      {value === index && (
        <Box sx={{ p: 2, height: 'auto' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const InvoiceTemplatePreview: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [activeColorSection, setActiveColorSection] = useState<'background' | 'font'>('background');
  const [loading, setLoading] = useState(false);
  
  // Color customization
  const [primaryColor, setPrimaryColor] = useState('#4776E6');
  const [secondaryColor, setSecondaryColor] = useState('#8E54E9');
  const [primaryFontColor, setPrimaryFontColor] = useState('#FFFFFF'); // Text in colored areas (headers, etc.)
  const [secondaryFontColor, setSecondaryFontColor] = useState('#333333'); // Regular text
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeColorControl, setActiveColorControl] = useState<'primary' | 'secondary' | 'primaryFont' | 'secondaryFont'>('primary');
  
  // For AkMo template
  const akmoRef = useRef<HTMLDivElement>(null);
  const [backgroundStyle, setBackgroundStyle] = useState<'gradient' | 'normal'>('gradient');
  const [akmoGradient, setAkmoGradient] = useState(`linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)`);
  
  // For Indian GST template
  const indianGstRef = useRef<HTMLDivElement>(null);
  
  // Add an effect to update the gradient when the background style changes
  React.useEffect(() => {
    if (backgroundStyle === 'gradient') {
      setAkmoGradient(`linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`);
    } else {
      setAkmoGradient(`linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%)`);
    }
  }, [backgroundStyle, primaryColor, secondaryColor]);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  const handleColorChange = (color: string) => {
    if (activeColorControl === 'primary') {
      setPrimaryColor(color);
      updateGradient(color, secondaryColor);
    } else if (activeColorControl === 'secondary') {
      setSecondaryColor(color);
      updateGradient(primaryColor, color);
    } else if (activeColorControl === 'primaryFont') {
      setPrimaryFontColor(color);
    } else if (activeColorControl === 'secondaryFont') {
      setSecondaryFontColor(color);
    }
  };
  
  const updateGradient = (primary: string, secondary: string) => {
    if (backgroundStyle === 'gradient') {
    setAkmoGradient(`linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`);
    } else {
      // For normal style, use only the primary color
      setAkmoGradient(`linear-gradient(135deg, ${primary} 0%, ${primary} 100%)`);
    }
  };
  
  const handleColorPickerToggle = (colorType: 'primary' | 'secondary' | 'primaryFont' | 'secondaryFont') => {
    setActiveColorControl(colorType);
    setShowColorPicker(!showColorPicker);
  };
  
  const handleResetColors = () => {
    setPrimaryColor('#4776E6');
    setSecondaryColor('#8E54E9');
    setPrimaryFontColor('#FFFFFF');
    setSecondaryFontColor('#333333');
    updateGradient('#4776E6', '#8E54E9');
  };
  
  const handleExportPdf = () => {
    setLoading(true);
    
    // Use a timeout to ensure UI updates before PDF generation
    setTimeout(() => {
      try {
        if (activeTab === 0 && akmoRef.current) {
          // Ensure template is fully rendered
          const template = document.getElementById('akmo-invoice-template');
          if (template) {
            exportInvoiceToPdf('akmo-invoice-template', 'AkMo_Invoice_Preview');
          } else {
            console.error('Template element not found');
          }
        } else if (activeTab === 1 && indianGstRef.current) {
          // Ensure template is fully rendered
          const template = document.getElementById('indian-gst-template');
          if (template) {
            exportInvoiceToPdf('indian-gst-template', 'Indian_GST_Invoice_Preview');
          } else {
            console.error('Template element not found');
          }
        }
      } catch (error) {
        console.error('Error exporting PDF:', error);
      } finally {
        setLoading(false);
      }
    }, 500); // Increased timeout for more reliable rendering
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 3, minHeight: 'calc(100vh - 64px)' }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2,
          height: 'auto',
          minHeight: '100%',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">Invoice Template Preview</Typography>
          <Box>
            <Button 
              variant="outlined" 
              onClick={() => navigate(-1)} 
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <Button 
              variant="contained" 
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleExportPdf}
              disabled={loading}
              sx={{ background: akmoGradient }}
            >
              Export PDF
            </Button>
          </Box>
        </Box>
        
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider', 
            mb: 1.5,
            '& .MuiTab-root': {
              fontWeight: 600,
              minWidth: 100,
              fontSize: '0.9rem',
              py: 0.75
            }
          }}
        >
          <Tab label="AkMo Invoice Format" />
          <Tab label="Indian GST Template" />
        </Tabs>
        
        <Grid container spacing={1} sx={{ flexGrow: 1 }}>
          <Grid item xs={12} md={3} sx={{ pb: 1 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 1.5,
                height: '100%', 
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 2
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Template Colors
              </Typography>
              <Tabs 
                value={activeColorSection === 'background' ? 0 : 1} 
                onChange={(_, newValue) => setActiveColorSection(newValue === 0 ? 'background' : 'font')}
                sx={{ 
                  mb: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    minWidth: 'auto',
                    fontWeight: 'medium',
                    fontSize: '0.9rem',
                    px: 2,
                    py: 0.75
                  },
                  '& .Mui-selected': {
                    color: primaryColor,
                    fontWeight: 'bold'
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: primaryColor,
                    height: 3
                  }
                }}
              >
                <Tab 
                  label="Background" 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    opacity: activeColorSection === 'background' ? 1 : 0.7,
                  }}
                />
                <Tab 
                  label="Font" 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    opacity: activeColorSection === 'font' ? 1 : 0.7,
                  }}
                />
              </Tabs>
              
              {/* Background Colors Section */}
              <Box sx={{ display: activeColorSection === 'background' ? 'block' : 'none' }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.9rem', mb: 1, fontWeight: 'medium' }}>
                    Style:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'row', mb: 1 }}>
                    <Box 
                      sx={{ 
                        mr: 2, 
                        display: 'flex', 
                        alignItems: 'center',
                        padding: '6px 10px',
                        borderRadius: 1,
                        backgroundColor: backgroundStyle === 'gradient' ? alpha(primaryColor, 0.1) : 'transparent',
                        border: `1px solid ${backgroundStyle === 'gradient' ? primaryColor : '#ddd'}`,
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setBackgroundStyle('gradient');
                        updateGradient(primaryColor, secondaryColor);
                      }}
                    >
                      <input
                        type="radio"
                        id="gradient-style"
                        name="background-style"
                        value="gradient"
                        checked={backgroundStyle === 'gradient'}
                        onChange={() => {
                          setBackgroundStyle('gradient');
                          updateGradient(primaryColor, secondaryColor);
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <label 
                        htmlFor="gradient-style" 
                        style={{ 
                          fontSize: '0.9rem', 
                          cursor: 'pointer',
                          fontWeight: backgroundStyle === 'gradient' ? 'bold' : 'normal'
                        }}
                      >
                        Gradient
                      </label>
                    </Box>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        padding: '6px 10px',
                        borderRadius: 1,
                        backgroundColor: backgroundStyle === 'normal' ? alpha(primaryColor, 0.1) : 'transparent',
                        border: `1px solid ${backgroundStyle === 'normal' ? primaryColor : '#ddd'}`,
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setBackgroundStyle('normal');
                        updateGradient(primaryColor, secondaryColor);
                      }}
                    >
                      <input
                        type="radio"
                        id="normal-style"
                        name="background-style"
                        value="normal"
                        checked={backgroundStyle === 'normal'}
                        onChange={() => {
                          setBackgroundStyle('normal');
                          updateGradient(primaryColor, secondaryColor);
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <label 
                        htmlFor="normal-style" 
                        style={{ 
                          fontSize: '0.9rem', 
                          cursor: 'pointer',
                          fontWeight: backgroundStyle === 'normal' ? 'bold' : 'normal'
                        }}
                      >
                        Normal
                      </label>
                    </Box>
                  </Box>
                </Box>
                
                <Typography variant="body2" gutterBottom sx={{ fontSize: '0.9rem' }}>
                  Primary Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    onClick={() => handleColorPickerToggle('primary')}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      backgroundColor: primaryColor,
                      cursor: 'pointer',
                      mr: 1.5,
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <TextField
                    size="small"
                    value={primaryColor}
                    onChange={(e) => {
                      setPrimaryColor(e.target.value);
                      updateGradient(e.target.value, secondaryColor);
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                </Box>
                
                <Typography variant="body2" gutterBottom sx={{ fontSize: '0.9rem' }}>
                  Secondary Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    onClick={() => handleColorPickerToggle('secondary')}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      backgroundColor: secondaryColor,
                      cursor: 'pointer',
                      mr: 1.5,
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <TextField
                    size="small"
                    value={secondaryColor}
                    onChange={(e) => {
                      setSecondaryColor(e.target.value);
                      updateGradient(primaryColor, e.target.value);
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontSize: '0.9rem' }}>
                    Preview:
                  </Typography>
                  <Box sx={{ 
                    height: 40, 
                    width: '100%',
                    borderRadius: 1,
                    background: akmoGradient,
                    border: '1px solid #ddd'
                  }} />
                </Box>
              </Box>
              
              {/* Font Colors Section */}
              <Box sx={{ display: activeColorSection === 'font' ? 'block' : 'none' }}>
                <Typography variant="body2" gutterBottom sx={{ fontSize: '0.9rem' }}>
                  Primary Font Color (for headers)
                  <Tooltip 
                    title="For text inside colored areas (business name, TAX INVOICE, table headers)" 
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" sx={{ ml: 0.5, p: 0.2 }}>
                      <InfoIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    onClick={() => handleColorPickerToggle('primaryFont')}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      backgroundColor: primaryFontColor,
                      cursor: 'pointer',
                      mr: 1.5,
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <TextField
                    size="small"
                    value={primaryFontColor}
                    onChange={(e) => {
                      setPrimaryFontColor(e.target.value);
                    }}
                    sx={{ flexGrow: 1 }}
                    placeholder="Primary Font Color"
                  />
                </Box>
                
                <Typography variant="body2" gutterBottom sx={{ fontSize: '0.9rem' }}>
                  Secondary Font Color (for body text)
                  <Tooltip 
                    title="For regular text throughout the invoice" 
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" sx={{ ml: 0.5, p: 0.2 }}>
                      <InfoIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    onClick={() => handleColorPickerToggle('secondaryFont')}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      backgroundColor: secondaryFontColor,
                      cursor: 'pointer',
                      mr: 1.5,
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <TextField
                    size="small"
                    value={secondaryFontColor}
                    onChange={(e) => {
                      setSecondaryFontColor(e.target.value);
                    }}
                    sx={{ flexGrow: 1 }}
                    placeholder="Secondary Font Color"
                  />
                </Box>
                </Box>
                
                {showColorPicker && (
                <Box 
                  sx={{ 
                    position: 'fixed',
                    zIndex: 1200,
                    width: '250px',
                    maxWidth: '90%',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.25)'
                  }}
                >
                    <Paper 
                    elevation={6}
                      sx={{ 
                        p: 2, 
                      width: '100%',
                      position: 'relative'
                      }}
                    >
                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontWeight: 'bold' }}>
                      Choose Color
                    </Typography>
                      <HexColorPicker 
                      color={
                        activeColorControl === 'primary' ? primaryColor : 
                        activeColorControl === 'secondary' ? secondaryColor : 
                        activeColorControl === 'primaryFont' ? primaryFontColor : 
                        secondaryFontColor
                      } 
                        onChange={handleColorChange} 
                        style={{ width: '100%', marginBottom: '10px' }}
                      />
                      <Button 
                        size="small" 
                      variant="contained"
                        fullWidth
                        onClick={() => setShowColorPicker(false)}
                      sx={{ mt: 2 }}
                      >
                        Close
                      </Button>
                    </Paper>
                  </Box>
                )}
              
              <Button 
                variant="outlined" 
                fullWidth 
                startIcon={<RefreshIcon />}
                onClick={handleResetColors}
                sx={{ mt: 1.5, fontSize: '0.9rem', py: 0.5 }}
              >
                Reset Colors
              </Button>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={9} sx={{ pb: 1 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                height: 'auto',
                minHeight: '50vh',
                maxHeight: '80vh', 
                overflow: 'auto',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 2
              }}
            >
              <TabPanel value={activeTab} index={0}>
                <div 
                  id="akmo-invoice-template" 
                  ref={akmoRef} 
                  style={{ 
                    transform: 'scale(0.75)',
                    transformOrigin: 'top center',
                    marginBottom: '30px',
                    background: '#ffffff',
                    width: '210mm', // Match A4 width for better PDF generation
                    maxWidth: '100%',
                    margin: '0 auto'
                  }}
                >
                  <AkMoInvoicingFormat
                    businessDetails={sampleBusinessDetails}
                    clientDetails={sampleClientDetails}
                    deliveryDetails={sampleDeliveryDetails}
                    invoiceDetails={sampleInvoiceDetails}
                    items={sampleInvoiceItems}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    fontColor={secondaryFontColor}
                    primaryFontColor={primaryFontColor}
                    backgroundStyle={backgroundStyle}
                  />
                </div>
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <div 
                  id="indian-gst-template" 
                  ref={indianGstRef} 
                  style={{ 
                    transform: 'scale(0.75)',
                    transformOrigin: 'top center',
                    marginBottom: '30px',
                    background: '#ffffff',
                    width: '210mm', // Match A4 width for better PDF generation
                    maxWidth: '100%',
                    margin: '0 auto'
                  }}
                >
                  <IndianGSTInvoiceTemplate
                    invoice={sampleGSTInvoice}
                    business={businessDetailsForGST}
                    showWatermark={false}
                    primaryColor={primaryColor}
                  />
                </div>
              </TabPanel>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default InvoiceTemplatePreview; 